import { Alert, Box, Button, Popover, Stack, Typography } from '@mui/material'
import type { TextNode } from '@shared/ast/astTypes'
import type { ContentProposal } from '@shared/contentProposal'
import { type ProposalWordBlock, type ProposalWordChange, sliceProposalText } from '@shared/proposalWordChanges'
import React, { useState } from 'react'

import type { InlineProposalReview } from './InlineProposal'

function RichText({ nodes }: { nodes: TextNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        const style: React.CSSProperties = {}
        for (const mark of node.marks ?? []) {
          if (mark === 'bold') {
            style.fontWeight = 'bold'
          } else if (mark === 'italic') {
            style.fontStyle = 'italic'
          } else if (mark === 'underline') {
            style.textDecoration = 'underline'
          } else if (typeof mark === 'object' && mark.type === 'font') {
            style.fontFamily = mark.family
          } else if (typeof mark === 'object' && mark.type === 'fontSize') {
            style.fontSize = mark.value
          } else if (typeof mark === 'object' && mark.type === 'color') {
            style.color = mark.value
          }
        }
        return (
          <span key={index} style={style}>
            {node.text}
          </span>
        )
      })}
    </>
  )
}

/** Read-only review layer: only word spans are highlighted, outside Lexical's saved state. */
export function WordProposal({
  item,
  block,
  onAction,
}: {
  item: ContentProposal
  block: ProposalWordBlock
  onAction: InlineProposalReview['onAction']
}) {
  const [selected, setSelected] = useState<{
    change: ProposalWordChange
    anchor: HTMLElement
    item: ContentProposal
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  let offset = 0
  const act = async (action: 'accept' | 'reject') => {
    if (!selected) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await onAction(selected.item, action, undefined, selected.change.id)
      setSelected(null)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'עדכון השינוי נכשל')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Box data-word-proposal={item.id} contentEditable={false} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {block.changes.map((change, index) => {
        const unchanged = sliceProposalText(block.before, offset, change.beforeStart)
        offset = change.beforeEnd
        return (
          <React.Fragment key={change.id}>
            <RichText nodes={unchanged} />
            <span
              role="button"
              tabIndex={0}
              className="proposal-word-change"
              data-proposal-change={change.id}
              aria-label={`שינוי ${index + 1}: ${change.before || 'תוספת'} ← ${change.after || 'מחיקה'}`}
              aria-haspopup="dialog"
              title="לחצו לאישור או לדחייה של השינוי"
              aria-disabled={busy || item.status !== 'pending'}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  event.currentTarget.click()
                }
              }}
              onClick={(event) => {
                if (busy || item.status !== 'pending') {
                  return
                }
                setError('')
                setSelected({ change, anchor: event.currentTarget, item })
              }}
            >
              {change.before && (
                <del data-word-deletion>
                  <RichText nodes={sliceProposalText(block.before, change.beforeStart, change.beforeEnd)} />
                </del>
              )}
              {change.after && (
                <ins data-word-insertion>
                  <RichText nodes={sliceProposalText(block.after, change.afterStart, change.afterEnd)} />
                </ins>
              )}
            </span>
          </React.Fragment>
        )
      })}
      <RichText nodes={sliceProposalText(block.before, offset, Infinity)} />
      <Popover
        open={!!selected}
        anchorEl={selected?.anchor}
        onClose={() => {
          if (!busy) {
            setSelected(null)
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box role="dialog" aria-label="בדיקת שינוי" dir="rtl" sx={{ p: 1.5, maxWidth: 340 }}>
          <Typography variant="caption">{item.source} · שינוי זה בלבד</Typography>
          <Stack direction="row" spacing={1}>
            <Button disabled={busy || !item.canApply || item.status !== 'pending'} onClick={() => void act('accept')}>
              אשר שינוי
            </Button>
            <Button disabled={busy || item.status !== 'pending'} onClick={() => void act('reject')}>
              דחה שינוי
            </Button>
          </Stack>
          {!item.canApply && <Alert severity="warning">ההצעה הוכנה בסביבת תוכן אחרת.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </Popover>
    </Box>
  )
}
